/**
 * useConfigDraft — Draft lifecycle management
 *
 * Manages: fetch versions, create draft from published, auto-save (3s debounce),
 * manual save, discard, publish. Single source of truth for draft state.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../api/client';
import { configToForm, formToConfig } from '../utils/configTransforms';

const AUTOSAVE_DELAY = 3000; // ms

export default function useConfigDraft(versionId, initialDraftCreatedFrom = null) {
  // --- State ---
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(null);         // current version record
  const [processes, setProcesses] = useState(null);      // form representation
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);    // 'saved' | 'saving' | 'error' | null
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Seeded from route state when createDraftFromVersion navigates to the new
  // draft's URL — lets the "Editing created a new draft" banner survive the
  // remount. Otherwise set inline by createDraftFromVersion below.
  const [draftCreatedFrom, setDraftCreatedFrom] = useState(initialDraftCreatedFrom);

  // Refs for auto-save
  const autoSaveTimer = useRef(null);
  const lastSavedSnapshot = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // --- Derived ---
  const isDraft = version && !version.published_at;
  const hasVersions = versions.length > 0;
  // Active = published version with the most recent published_at timestamp
  const activeVersion = versions
    .filter(v => v.published_at)
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))[0] || null;

  // --- Fetch ---
  const fetchVersions = useCallback(async () => {
    try {
      const res = await api.get('/portal/config/versions');
      if (isMounted.current) setVersions(res.data);
      return res.data;
    } catch (err) {
      if (isMounted.current) {
        setError(err.response?.data?.message || 'Failed to load versions.');
      }
      return [];
    }
  }, []);

  const fetchVersion = useCallback(async (id) => {
    try {
      const res = await api.get(`/portal/config/versions/${id}`);
      return res.data;
    } catch (err) {
      if (isMounted.current) {
        setError(err.response?.data?.message || 'Failed to load version.');
      }
      return null;
    }
  }, []);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      const versionsList = await fetchVersions();
      if (cancelled) return;

      // If no specific versionId is requested, fall back to the active
      // (latest published) version so /config can render the editor in place
      // without redirecting to a versioned URL.
      let idToLoad = versionId;
      if (!idToLoad && versionsList.length > 0) {
        const active = versionsList
          .filter(v => v.published_at)
          .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))[0];
        idToLoad = active?.id || versionsList[0]?.id;
      }

      if (idToLoad) {
        const v = await fetchVersion(idToLoad);
        if (cancelled) return;
        if (v) {
          setVersion(v);
          const formData = configToForm(v.config);
          setProcesses(formData);
          lastSavedSnapshot.current = JSON.stringify(formData);
        }
      }
      if (isMounted.current) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [versionId, fetchVersions, fetchVersion]);

  // --- Auto-save (only for drafts) ---
  useEffect(() => {
    if (!isDraft || !processes || !version) return;

    const currentSnapshot = JSON.stringify(processes);
    if (currentSnapshot === lastSavedSnapshot.current) return;

    // Clear existing timer
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      if (!isMounted.current) return;
      setSaveStatus('saving');
      try {
        const config = formToConfig(processes);
        await api.patch(`/portal/config/versions/${version.id}`, { config });
        if (isMounted.current) {
          lastSavedSnapshot.current = currentSnapshot;
          setSaveStatus('saved');
          setLastSavedAt(new Date());
        }
      } catch (err) {
        if (isMounted.current) {
          setSaveStatus('error');
        }
      }
    }, AUTOSAVE_DELAY);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [processes, isDraft, version]);

  // --- Actions ---

  /** Create a new draft from a published version. Returns the new draft's id. */
  const createDraftFromVersion = useCallback(async (sourceVersionId) => {
    setError('');
    setSaving(true);
    try {
      const source = await fetchVersion(sourceVersionId);
      if (!source) throw new Error('Source version not found');

      const res = await api.post('/portal/config/versions', { config: source.config });
      const newVersion = res.data;

      if (isMounted.current) {
        setVersion(newVersion);
        setDraftCreatedFrom(source.version_number);
        const formData = configToForm(newVersion.config || source.config);
        setProcesses(formData);
        lastSavedSnapshot.current = JSON.stringify(formData);
        // Refresh versions list
        await fetchVersions();
      }
      return newVersion.id;
    } catch (err) {
      if (isMounted.current) {
        setError(err.response?.data?.message || 'Failed to create draft.');
      }
      return null;
    } finally {
      if (isMounted.current) setSaving(false);
    }
  }, [fetchVersion, fetchVersions]);

  /** Save draft immediately (for Save & Close). */
  const saveDraft = useCallback(async () => {
    if (!isDraft || !version || !processes) return false;
    setError('');
    setSaving(true);
    setSaveStatus('saving');
    try {
      const config = formToConfig(processes);
      await api.patch(`/portal/config/versions/${version.id}`, { config });
      if (isMounted.current) {
        lastSavedSnapshot.current = JSON.stringify(processes);
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        setSuccess(`Draft v${version.version_number} saved.`);
      }
      return true;
    } catch (err) {
      if (isMounted.current) {
        setSaveStatus('error');
        setError(err.response?.data?.message || 'Failed to save draft.');
      }
      return false;
    } finally {
      if (isMounted.current) setSaving(false);
    }
  }, [isDraft, version, processes]);

  /** Save & Publish. */
  const saveAndPublish = useCallback(async () => {
    if (!version || !processes) return false;
    setError('');
    setSaving(true);
    try {
      const config = formToConfig(processes);
      if (isDraft) {
        // Patch then publish
        await api.patch(`/portal/config/versions/${version.id}`, { config });
        await api.put(`/portal/config/versions/${version.id}/publish`);
      } else {
        // Create new version then publish
        const res = await api.post('/portal/config/versions', { config });
        await api.put(`/portal/config/versions/${res.data.id}/publish`);
      }
      if (isMounted.current) {
        setSuccess(`v${version.version_number} saved and published — extensions will pick it up on their next event.`);
        lastSavedSnapshot.current = JSON.stringify(processes);
      }
      return true;
    } catch (err) {
      if (isMounted.current) {
        setError(err.response?.data?.message || 'Failed to publish.');
      }
      return false;
    } finally {
      if (isMounted.current) setSaving(false);
    }
  }, [version, processes, isDraft]);

  /** Discard draft — clear local state and navigate away.
   *  The draft row remains in the DB as an unpublished version (harmless).
   *  A proper DELETE endpoint can be added later if cleanup is desired. */
  const discardDraft = useCallback(async () => {
    if (isMounted.current) {
      setVersion(null);
      setProcesses(null);
      setDraftCreatedFrom(null);
      setSaveStatus(null);
      setError('');
      setSuccess('');
    }
    return true;
  }, []);

  /** Toggle an operational boolean on any version (no draft needed). */
  const toggleField = useCallback(async (path, value, currentProcesses) => {
    if (!version) return;
    // Apply optimistically
    const match = path.match(/^processes\[(\d+)\](?:\.steps\[(\d+)\](?:\.conditions\[(\d+)\])?)?\.(\w+)$/);
    if (!match) return;

    const [, pi, si, ci, field] = match;
    const prev = JSON.parse(JSON.stringify(currentProcesses));
    const next = JSON.parse(JSON.stringify(currentProcesses));

    if (ci !== undefined) {
      next[parseInt(pi)].steps[parseInt(si)].conditions[parseInt(ci)][field] = value;
    } else if (si !== undefined) {
      next[parseInt(pi)].steps[parseInt(si)][field] = value;
    } else {
      next[parseInt(pi)][field] = value;
    }

    setProcesses(next);

    try {
      await api.patch(`/portal/config/versions/${version.id}/toggles`, {
        toggles: [{ path, value }]
      });
    } catch (err) {
      // Revert on failure
      if (isMounted.current) {
        setProcesses(prev);
        setError(err.response?.data?.message || 'Failed to update toggle.');
      }
    }
  }, [version]);

  return {
    // State
    versions,
    version,
    processes,
    setProcesses,
    loading,
    saving,
    error,
    setError,
    success,
    setSuccess,
    saveStatus,
    lastSavedAt,
    draftCreatedFrom,

    // Derived
    isDraft,
    hasVersions,
    activeVersion,

    // Actions
    fetchVersions,
    fetchVersion,
    createDraftFromVersion,
    saveDraft,
    saveAndPublish,
    discardDraft,
    toggleField,
  };
}
